import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

// The Lobbero IQ chat is a single, globally-mounted surface (a floating modal),
// but it is opened from several places: its own FAB, the "Shift report" menu tile,
// and potentially any future entry point. This tiny context is the shared open /
// mode state so all of them drive the ONE chat instance rather than each owning a
// copy of the thread. It carries nothing else — building selection and the message
// thread live inside <IqChat> so they persist across open/close without a wider
// BuildingProvider refactor.

export type IqChatMode = 'float' | 'full';

interface IqChatContextValue {
  isOpen: boolean;
  mode: IqChatMode;
  /**
   * `prefill` seeds the composer with a question the OPENER has composed.
   *
   * Added for "Ask IQ about this view" on the Dashboard. POST /iq/ask takes
   * `{building_id?, question, tz}` and nothing else — there is no filter-context
   * or aggregates parameter — so the context has to travel INSIDE the question.
   * The Dashboard composes it from the filter and the figures that are
   * actually on screen, which is what stops an answer citing numbers the reader
   * cannot see.
   *
   * Additive and optional: every existing caller passes nothing and behaves
   * exactly as before. The composer clears it once it has been consumed, so
   * re-opening the chat does not re-seed a stale question.
   */
  prefill: string | null;
  open: (mode?: IqChatMode, prefill?: string) => void;
  consumePrefill: () => void;
  close: () => void;
  toggle: () => void;
  setMode: (mode: IqChatMode) => void;
}

const IqChatContext = createContext<IqChatContextValue | null>(null);

export const IqChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<IqChatMode>('float');
  const [prefill, setPrefill] = useState<string | null>(null);

  const open = useCallback((next?: IqChatMode, question?: string) => {
    if (next) setMode(next);
    if (question) setPrefill(question);
    setIsOpen(true);
  }, []);
  const consumePrefill = useCallback(() => setPrefill(null), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const value = useMemo(
    () => ({ isOpen, mode, prefill, open, consumePrefill, close, toggle, setMode }),
    [isOpen, mode, prefill, open, consumePrefill, close, toggle]
  );

  return (
    <IqChatContext.Provider value={value}>{children}</IqChatContext.Provider>
  );
};

// Fails loud rather than silently no-opening a tile: a consumer outside the
// provider is a wiring bug, not a runtime state.
export const useIqChat = (): IqChatContextValue => {
  const ctx = useContext(IqChatContext);
  if (!ctx) {
    throw new Error('useIqChat must be used within an IqChatProvider');
  }

  return ctx;
};
